/** Item Revisions — a controlled document, not a field.
 *
 * GHERP had this right and we nearly replaced it with something worse.
 * `TrnHdrIR` is a proper document: a number, a date, the item, the revision,
 * remarks, and a full authorisation trail. Better than most small ERPs manage.
 *
 * What was wrong is that `MstItem.REVNO` existed as well — free text on the
 * item master, editable by anyone, connected to the document by nothing. An
 * item's current revision had two answers and they drifted. Ledger D26.
 *
 * So there is **no revision field on the item master here**. The current
 * revision is derived from the latest AUTHORISED document, which means
 * changing a revision has exactly one route: raise one, and have somebody
 * authorise it.
 *
 * `authorise` is its own permission. The draughtsman proposes; someone
 * answerable approves. Handing both to one person by default would rebuild
 * D26's hole from the other side.
 */

import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { SearchSelect } from '@/components/SearchSelect'
import { useObjectRights } from '@/lib/rights'

interface Revision {
  revision_id: string
  doc_no: string
  doc_date: string
  item_id: string
  item_name: string
  revision: string
  status: 'Draft' | 'Authorised' | 'Cancelled'
  remarks: string | null
  reason: string | null
  authorised_by: string | null
  authorised_at: string | null
  version: number
  files: number
}
interface Item { item_id: string; item_name: string }

/** DD-MM-YYYY, app-wide. UI_TASTE.md. */
function asDate(v: string | null): string {
  if (!v) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v)
  return m ? `${m[3]}-${m[2]}-${m[1]}` : v
}

const today = () => new Date().toISOString().slice(0, 10)

export function ItemRevisionScreen() {
  const [rows, setRows] = useState<Revision[]>([])
  const [items, setItems] = useState<Item[]>([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  // design.item_revision.add / authorise / cancel — design/index.py.
  const rights = useObjectRights('design', 'item_revision')

  const [form, setForm] = useState<null | {
    item_id: string; revision: string; doc_date: string;
    reason: string; remarks: string
  }>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get<{ rows: Revision[] }>(
        '/design/item-revisions',
        { q: search || undefined, status: statusFilter || undefined })
      setRows(r.rows ?? [])
      setProblem(null)
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message
                                       : 'The list could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [search, statusFilter])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    api.get<{ rows: Item[] }>('/masters/items')
      .then(r => setItems(r.rows ?? []))
      .catch(() => setItems([]))
  }, [])

  async function act(r: Revision, what: 'authorise' | 'cancel') {
    // A revision is what the item IS once released, so releasing it gets a
    // confirmation. Cancelling a draft does not — it destroys nothing.
    if (what === 'authorise' && !window.confirm(
      `Authorise ${r.doc_no}?\n\n${r.item_name} becomes revision ` +
      `${r.revision} from now on. An authorised revision cannot be changed ` +
      `or cancelled — it is superseded by a later one.`)) return

    setBusy(r.revision_id)
    try {
      await api.create(`/design/item-revisions/${r.revision_id}/${what}`,
                       { version: r.version })
      await load()
    } catch (e) {
      setProblem(e instanceof Error ? e.message
                                    : 'That could not be completed.')
    } finally {
      setBusy(null)
    }
  }

  async function save() {
    if (!form) return
    setBusy('new')
    try {
      await api.create('/design/item-revisions', form)
      setForm(null)
      await load()
    } catch (e) {
      setProblem(e instanceof Error ? e.message
                                    : 'The revision could not be saved.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="screen">
      <div className="page-head">
        <div>
          <h1 className="page-title">Item Revisions</h1>
          <p className="page-sub">
            {loading ? 'Loading…' : `${rows.length} of ${rows.length}`}
          </p>
        </div>
      </div>

      {problem && <div className="form-errors">{problem}</div>}

      {form && (
        <div className="card">
          <div className="card-head"><h2>New Item Revision</h2></div>
          <div className="form-grid">
            <div className="form-field">
              <label>Item <span className="req">*</span></label>
              <SearchSelect
                value={form.item_id}
                emptyLabel="— choose an item —"
                onChange={v => setForm({ ...form, item_id: v })}
                options={items.map(i => ({
                  value: i.item_id,
                  label: `${i.item_id} — ${i.item_name}` }))}
              />
              {items.length === 0 && (
                <div className="form-help">
                  No items are available to you.
                </div>
              )}
            </div>

            <div className="form-field">
              <label>Revision <span className="req">*</span></label>
              <input
                value={form.revision}
                onChange={e => setForm({ ...form, revision: e.target.value })}
                placeholder="A"
              />
              <div className="form-help">
                One revision per item — the same one twice is refused
              </div>
            </div>

            <div className="form-field">
              <label>Date <span className="req">*</span></label>
              <input
                type="date"
                value={form.doc_date}
                onChange={e => setForm({ ...form, doc_date: e.target.value })}
              />
            </div>

            <div className="form-field">
              <label>Reason</label>
              <input
                value={form.reason}
                onChange={e => setForm({ ...form, reason: e.target.value })}
                placeholder="Customer change, design correction…"
              />
            </div>

            <div className="form-field span-2">
              <label>Remarks</label>
              <textarea
                value={form.remarks}
                onChange={e => setForm({ ...form, remarks: e.target.value })}
                placeholder="What changed on the drawing"
              />
            </div>
          </div>

          <p className="form-note">
            Saved as a <strong>Draft</strong>. It becomes the item's revision
            only when somebody authorises it.
          </p>

          <div className="form-actions">
            <button className="btn btn-primary" onClick={() => void save()}
                    disabled={busy === 'new'}>
              {busy === 'new' ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn-secondary" onClick={() => setForm(null)}
                    disabled={busy === 'new'}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-head toolbar">
          <input
            className="search"
            placeholder="Search by document, item or revision…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="Draft">Draft</option>
            <option value="Authorised">Authorised</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          {rights.can('add') && (
            <button
              className="btn btn-primary"
              onClick={() => setForm({
                item_id: '', revision: '', doc_date: today(),
                reason: '', remarks: '',
              })}
            >New Item Revision</button>
          )}
        </div>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Document</th><th>Date</th><th>Item</th>
                <th>Revision</th><th>Reason</th><th>Status</th>
                <th>Authorised</th><th></th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="empty-cell">
                  {search || statusFilter
                    ? 'Nothing matches that.'
                    : 'No revisions yet. A revision is how a drawing change ' +
                      'is recorded — and the only way an item’s revision ' +
                      'can change.'}
                </td></tr>
              )}
              {rows.map(r => (
                <tr key={r.revision_id}>
                  <td>{r.doc_no}</td>
                  <td>{asDate(r.doc_date)}</td>
                  <td>{r.item_id} — {r.item_name}</td>
                  <td><strong>{r.revision}</strong></td>
                  <td>{r.reason ?? '—'}</td>
                  <td>
                    <span className={`badge badge-${r.status.toLowerCase()}`}>
                      {r.status}
                    </span>
                  </td>
                  <td>
                    {r.authorised_at
                      ? `${asDate(r.authorised_at)}${r.authorised_by ? ` · ${r.authorised_by}` : ''}`
                      : '—'}
                  </td>
                  <td className="row-actions">
                    {/* Only a Draft offers actions. An authorised revision is
                        what the item is; a cancelled one is history. Neither
                        has anything to do here, so neither shows a button
                        that would only refuse. */}
                    {r.status === 'Draft' && (
                      <>
                        {rights.can('authorise') && (
                          <button className="link"
                                  disabled={busy === r.revision_id}
                                  onClick={() => void act(r, 'authorise')}>
                            Authorise
                          </button>
                        )}
                        {rights.can('cancel') && (
                          <button className="link"
                                  disabled={busy === r.revision_id}
                                  onClick={() => void act(r, 'cancel')}>
                            Cancel
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
